import { useEffect, useRef, useState } from 'react';

interface ModelViewerProps {
  /** URL of the model file (same-origin /uploads/... path). */
  url: string;
  /** Lowercased file extension, e.g. "stl" | "obj" | "glb" | "gltf" | "3mf". */
  ext: string;
}

// Interactive 3D viewer for common mesh formats. three.js is loaded on demand
// (dynamic import) so it only ships to users who actually open a 3D file.
export function ModelViewer({ url, ext }: ModelViewerProps) {
  const mountRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let disposed = false;
    let cleanup: (() => void) | undefined;

    (async () => {
      try {
        const THREE = await import('three');
        const { OrbitControls } = await import('three/examples/jsm/controls/OrbitControls.js');

        const mount = mountRef.current;
        if (!mount || disposed) return;

        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf5f1ea); // cream, matches app bg

        const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 10000);
        const renderer = new THREE.WebGLRenderer({ antialias: true });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        mount.appendChild(renderer.domElement);

        scene.add(new THREE.AmbientLight(0xffffff, 0.7));
        const dir = new THREE.DirectionalLight(0xffffff, 1.2);
        dir.position.set(1, 2, 1.5);
        scene.add(dir);
        const dir2 = new THREE.DirectionalLight(0xffffff, 0.4);
        dir2.position.set(-1.5, -1, -1);
        scene.add(dir2);

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;

        const material = new THREE.MeshStandardMaterial({ color: 0xc77d3f, metalness: 0.1, roughness: 0.75 });

        // Load by format
        let object: import('three').Object3D;
        if (ext === 'stl') {
          const { STLLoader } = await import('three/examples/jsm/loaders/STLLoader.js');
          const geo = await new STLLoader().loadAsync(url);
          geo.computeVertexNormals();
          object = new THREE.Mesh(geo, material);
        } else if (ext === 'obj') {
          const { OBJLoader } = await import('three/examples/jsm/loaders/OBJLoader.js');
          object = await new OBJLoader().loadAsync(url);
          object.traverse(o => { if ((o as import('three').Mesh).isMesh) (o as import('three').Mesh).material = material; });
        } else if (ext === '3mf') {
          const { ThreeMFLoader } = await import('three/examples/jsm/loaders/3MFLoader.js');
          object = await new ThreeMFLoader().loadAsync(url);
        } else {
          const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
          const gltf = await new GLTFLoader().loadAsync(url);
          object = gltf.scene;
        }
        if (disposed) return;
        scene.add(object);

        // Fit camera to the model bounds
        const box = new THREE.Box3().setFromObject(object);
        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        object.position.sub(center); // center at origin
        camera.position.set(maxDim * 0.9, maxDim * 0.7, maxDim * 1.1);
        camera.near = maxDim / 100;
        camera.far = maxDim * 20;
        camera.updateProjectionMatrix();
        controls.target.set(0, 0, 0);

        const grid = new THREE.GridHelper(maxDim * 2, 20, 0xb8a68f, 0xd9cdbd);
        grid.position.y = -size.y / 2;
        scene.add(grid);

        let raf = 0;
        const animate = () => {
          raf = requestAnimationFrame(animate);
          controls.update();
          renderer.render(scene, camera);
        };
        animate();

        const onResize = () => {
          if (!mount) return;
          camera.aspect = mount.clientWidth / mount.clientHeight;
          camera.updateProjectionMatrix();
          renderer.setSize(mount.clientWidth, mount.clientHeight);
        };
        window.addEventListener('resize', onResize);

        setLoading(false);
        cleanup = () => {
          cancelAnimationFrame(raf);
          window.removeEventListener('resize', onResize);
          controls.dispose();
          renderer.dispose();
          mount.removeChild(renderer.domElement);
        };
      } catch (e) {
        if (!disposed) {
          setLoading(false);
          setError(e instanceof Error ? e.message : 'Failed to load 3D model');
        }
      }
    })();

    return () => { disposed = true; cleanup?.(); };
  }, [url, ext]);

  return (
    <div className="relative w-full h-full min-h-[420px]">
      <div ref={mountRef} className="absolute inset-0" />
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center text-gray-500 text-sm">
          <span className="material-symbols-outlined animate-spin mr-2">progress_activity</span>
          Loading 3D model…
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 text-sm gap-2">
          <span className="material-symbols-outlined text-3xl text-gray-300">deployed_code_alert</span>
          <p>Couldn't render this model: {error}</p>
        </div>
      )}
      {!loading && !error && (
        <div className="absolute bottom-2 left-2 text-[11px] text-gray-500 bg-white/70 rounded px-2 py-0.5 pointer-events-none">
          Drag to rotate · scroll to zoom · right-drag to pan
        </div>
      )}
    </div>
  );
}
